import { useState, useEffect, useCallback } from "react";
import axios from "axios";

/** Canonical display order — matches `bxi-dashboard/src/components/Marketplace/Categories.jsx`. */
const COMPANY_TYPE_SEQUENCE = [
  "Media",
  "Hotel",
  "QSR",
  "Lifestyle",
  "Textile",
  "Mobility",
  "Entertainment & Events",
  "FMCG",
  "Electronics",
  "Airline Tickets",
  "Office Supply",
  "Other",
];

function sequenceOrder(name) {
  const i = COMPANY_TYPE_SEQUENCE.indexOf(name);
  return i === -1 ? Number.MAX_SAFE_INTEGER : i;
}

function normalizeCarouselTypes(rawList) {
  const list = Array.isArray(rawList) ? rawList : [];
  return list
    .map((t) => {
      const name = String(t?.CompanyTypeName ?? "").trim();
      if (!name || t?._id == null) return null;
      return {
        _id: String(t._id),
        name,
        CompanyTypeName: name,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const d = sequenceOrder(a.name) - sequenceOrder(b.name);
      if (d !== 0) return d;
      return a.name.localeCompare(b.name);
    });
}

/**
 * Company types for admin filters (matches product `ProductType` and member `companyType`).
 * GET `company_type/get_companyTypes_for_carousel` — same source as bxi-dashboard marketplace carousel.
 */
export function useCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get("company_type/get_companyTypes_for_carousel", {
        withCredentials: true,
      });
      const raw = Array.isArray(res?.data) ? res.data : res?.data?.body ?? [];
      setCategories(normalizeCarouselTypes(raw));
    } catch (err) {
      setError(err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  /** Options for dropdowns: [{ value: '', label: 'All' }, { value: _id, label: name }, ...] */
  const categoryOptions = [
    { value: "", label: "All" },
    ...(categories || []).map((c) => ({
      value: c._id ?? "",
      label: c.name ?? c.CompanyTypeName ?? String(c._id ?? ""),
    })).filter((opt) => opt.value),
  ];

  return { categories, categoryOptions, loading, error, refetch: fetchCategories };
}
